import {
  forwardRef,
  memo,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { observer } from "mobx-react-lite";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Calendar, Pencil, Tag, UserPlus, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import type { PMIssue } from "@/domains/pm/types";
import { DisciplineBadges, AssigneeAvatar } from "@/domains/pm/components/shared";
import { DocChip, DocChipCollapsed } from "@/components/chat/SidebarChat/DocLink/DocChip";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { useOpenLinkedDoc } from "@/domains/pm/hooks/useOpenLinkedDoc";
import { isIssueOverdue } from "@/domains/pm/utils/dueDate";
import { cn } from "@/lib/utils";
import { isTitleUnchanged } from "@/domains/pm/issueTitle";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { IssueContextMenu } from "./IssueContextMenu";
import { ChecklistProgressBadge } from "./ChecklistProgressBadge";
import { AssigneeCell, DisciplineCell, DueDateCell } from "./IssueRowCellEditors";
import { useBlockedDragNotice } from "@/hooks/useBlockedDragNotice";
import { isDraftIssue } from "@/domains/pm/issueDraft";
import { DraftIssueMarker, DraftIssueTitle } from "./IssueDraftViewParts";
import { draftActivationProps, useDraftIssueLabel } from "./issueDraftView";

interface KanbanCardProps {
  issue: PMIssue;
  buildingShortCode?: string;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  onSelect: (issue: PMIssue) => void;
  /** False where the user may not change this issue (MANAGE_PM on its
   *  project): the card can still be opened, but not picked up. */
  draggable?: boolean;
  /** Called when the user tries to drag a card `draggable` forbids. */
  onBlockedDrag?: (issue: PMIssue) => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${d.getFullYear()}`;
}

/**
 * One issue on the board: sortable within its column and across columns.
 *
 * While it is dragged the card itself stays in the list as a faded placeholder
 * showing where it will land, and the board's DragOverlay draws the copy that
 * follows the pointer (KanbanCardFace) — so crossing columns never remounts
 * the thing under the user's hand.
 *
 * Memoised, so opening an issue or reloading the list redraws only the cards
 * whose issue changed. A drag re-renders every card through dnd-kit's own
 * context, which is what lets the others make room — so that context is read
 * only by the thin shell inside, and the right-click menu around it and the
 * face within it sit the drag out. Re-rendering the menu for all of them on
 * every pick-up and column crossing is what made the board stall.
 */
export const KanbanCard = memo(function KanbanCard({
  issue,
  buildingShortCode,
  openViewer,
  onSelect,
  draggable = true,
  onBlockedDrag,
}: KanbanCardProps) {
  return (
    <IssueContextMenu issue={issue}>
      <SortableCardShell
        issue={issue}
        onSelect={onSelect}
        draggable={draggable}
        onBlockedDrag={onBlockedDrag}
      >
        <KanbanCardFace
          issue={issue}
          buildingShortCode={buildingShortCode}
          openViewer={openViewer}
          editable={draggable}
        />
      </SortableCardShell>
    </IssueContextMenu>
  );
});

/**
 * A new issue still being written, at the top of its status column. Not
 * sortable and not a drop target — it is outside the column's SortableContext,
 * so no drag, reorder or stored column order can reach its id — and it has no
 * right-click menu or in-place editors. A click (or Enter) opens it in the
 * draft panel, where it is edited.
 */
export const KanbanDraftCard = observer(function KanbanDraftCard({
  issue,
  isActive,
  isSaving,
  onSelect,
  openViewer,
}: {
  issue: PMIssue;
  isActive: boolean;
  isSaving: boolean;
  onSelect: (draftId: number) => void;
  openViewer: KanbanCardProps["openViewer"];
}) {
  const label = useDraftIssueLabel();
  return (
    <div
      data-draft-issue={issue.id}
      {...draftActivationProps(() => onSelect(issue.id), label(issue))}
      aria-busy={isSaving || undefined}
      aria-disabled={isSaving || undefined}
      className={cn(
        "select-none rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isSaving ? "cursor-default opacity-70" : "cursor-pointer",
        isActive && "ring-2 ring-primary/40"
      )}
    >
      <KanbanCardFace issue={issue} openViewer={openViewer} saving={isSaving} />
    </div>
  );
});

type TriggerProps = Omit<HTMLAttributes<HTMLDivElement>, "onSelect" | "draggable">;
type PointerHandlers = Partial<Record<string, ((event: never) => void) | undefined>>;

interface SortableCardShellProps
  extends TriggerProps,
    Pick<KanbanCardProps, "issue" | "onSelect" | "draggable" | "onBlockedDrag"> {
  children: ReactNode;
}

/**
 * The card's draggable node. It also receives the context-menu trigger's props
 * (the trigger renders `asChild`), so every event handler from the trigger,
 * the sortable and the blocked-drag notice is composed here: the card's own
 * handlers first, then the trigger's — the order Radix's Slot used when this
 * node was the trigger's direct child.
 */
const SortableCardShell = forwardRef<HTMLDivElement, SortableCardShellProps>(
  function SortableCardShell(
    { issue, onSelect, draggable = true, onBlockedDrag, children, ...triggerProps },
    forwardedRef
  ) {
    const blocked = useBlockedDragNotice(
      () => onBlockedDrag?.(issue),
      !draggable && !!onBlockedDrag
    );
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: issue.id,
      data: { kind: "card", status: issue.status },
      disabled: !draggable,
    });
    const setRefs = (node: HTMLDivElement | null) => {
      setNodeRef(node);
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    };

    return (
      <div
        {...triggerProps}
        {...attributes}
        {...composeHandlers(
          listeners as PointerHandlers | undefined,
          blocked.handlers as PointerHandlers,
          triggerProps as PointerHandlers
        )}
        ref={setRefs}
        style={{ ...triggerProps.style, transform: CSS.Translate.toString(transform), transition }}
        onClick={(e) => {
          triggerProps.onClick?.(e);
          if (blocked.consumeClick()) return;
          // The second click of a double-click would close the issue the
          // first one just opened (a click on the open issue closes it).
          if (e.detail > 1) return;
          onSelect(issue);
        }}
        className={cn(
          triggerProps.className,
          "select-none rounded-lg motion-reduce:!transition-none",
          draggable && "cursor-grab active:cursor-grabbing",
          isDragging && "opacity-40"
        )}
      >
        {children}
      </div>
    );
  }
);

/** One handler per event name, calling each source's in turn. */
function composeHandlers(...sources: Array<PointerHandlers | undefined>): PointerHandlers {
  const names = new Set(
    sources.flatMap((source) =>
      Object.keys(source ?? {}).filter(
        (name) => /^on[A-Z]/.test(name) && typeof source?.[name] === "function"
      )
    )
  );
  names.delete("onClick");
  return Object.fromEntries(
    [...names].map((name) => [
      name,
      (event: never) => {
        for (const source of sources) source?.[name]?.(event);
      },
    ])
  );
}

const stop = (e: SyntheticEvent) => e.stopPropagation();

/**
 * A control on the card. The card itself is the drag handle and the "open
 * issue" target, so everything that happens inside an editor — its own
 * clicks, a pointer moving inside its popover, Space/Enter in its inputs —
 * must stop here. React bubbles events out of portals too, so this also
 * covers the popovers the cell editors open.
 */
function CardControl({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={className} onPointerDown={stop} onKeyDown={stop} onClick={stop}>
      {children}
    </div>
  );
}

/**
 * An empty field on the card: a small muted icon, always there, so the card
 * does not reserve blank space for it or change shape on hover. The label is
 * its tooltip and accessible name.
 */
function EmptyCardField({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span
      title={label}
      className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground/60 transition-colors hover:text-foreground"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** The card's content, shared by the card in the list and the copy that
 *  follows the pointer. */
interface KanbanCardFaceProps
  extends Pick<KanbanCardProps, "issue" | "buildingShortCode" | "openViewer"> {
  /** The copy under the pointer during a drag. */
  lifted?: boolean;
  /** Editing is disabled on the dragged copy and without project permission. */
  editable?: boolean;
  /** A draft whose save is running — its marker says so. */
  saving?: boolean;
}
interface CardFieldsProps {
  issue: PMIssue;
  interactive: boolean;
  hasTags: boolean;
}

export const KanbanCardFace = observer(function KanbanCardFace({
  issue,
  buildingShortCode,
  openViewer,
  lifted = false,
  editable = false,
  saving = false,
}: KanbanCardFaceProps) {
  const prefix = buildingShortCode
    ? `#${buildingShortCode}-${issue.issue_number}`
    : `#${issue.issue_number}`;

  // A draft has no number and nothing to edit in place: it is edited in the
  // draft panel, and every in-place editor writes against a real id.
  const draft = isDraftIssue(issue);
  const interactive = editable && !lifted && !draft;
  const hasTags = (issue.disciplines?.length ?? 0) > 0;

  return (
    <div
      className={cn(
        "group/card rounded-lg border bg-white p-3 transition-shadow hover:shadow-md",
        lifted && "rotate-1 cursor-grabbing shadow-xl"
      )}
    >
      {/* ID */}
      <div className="mb-1.5 flex items-center justify-between gap-1.5">
        {draft ? (
          <DraftIssueMarker saving={saving} />
        ) : (
          <span className="text-[10px] font-medium text-muted-foreground">{prefix}</span>
        )}
      </div>

      {/* Title — the pencil edits it in place; a click still opens the issue. */}
      {interactive ? (
        <CardTitle issue={issue} />
      ) : draft ? (
        <p className="mb-2 line-clamp-4 min-w-0 whitespace-pre-wrap text-sm font-medium leading-snug [overflow-wrap:anywhere]">
          <DraftIssueTitle issue={issue} />
        </p>
      ) : (
        // Long unbroken titles — a filename is the usual one — wrap inside the card.
        <p className="mb-2 line-clamp-4 min-w-0 whitespace-pre-wrap text-sm font-medium leading-snug [overflow-wrap:anywhere]">
          {issue.title}
        </p>
      )}

      {/* Disciplines — their own row once there are any; an empty field is
          an icon in the footer instead. */}
      <CardDisciplines issue={issue} interactive={interactive} hasTags={hasTags} />

      {/* Linked Documents */}
      <CardDocuments issue={issue} openViewer={openViewer} />

      {/* Footer: assignees + due date */}
      <div className="mt-1 flex items-center justify-between gap-2">
        <CardAssignees issue={issue} interactive={interactive} hasTags={hasTags} />
        <div className="flex items-center gap-2">
          <ChecklistProgressBadge issue={issue} className="text-[10px]" />
          <CardDueDate issue={issue} interactive={interactive} />
        </div>
      </div>
    </div>
  );
});

const CardDisciplines = observer(function CardDisciplines({
  issue,
  interactive,
  hasTags,
}: CardFieldsProps) {
  return (
    <>
      {hasTags &&
        (interactive ? (
          <CardControl className="-ml-1 mb-2">
            <DisciplineCell issue={issue} />
          </CardControl>
        ) : (
          <div className="mb-2 flex flex-wrap gap-1">
            <DisciplineBadges
              disciplines={issue.disciplines ?? []}
              size="sm"
              variant="outline"
              max={3}
            />
          </div>
        ))}
    </>
  );
});

const CardDocuments = observer(function CardDocuments({
  issue,
  openViewer,
}: Pick<KanbanCardProps, "issue" | "openViewer">) {
  const {
    canOpen,
    nonOpenableFilenames,
    open: openDoc,
  } = useOpenLinkedDoc(issue.building_id, openViewer);
  return (
    <>
      {issue.linked_documents && issue.linked_documents.length > 0 && (
        <div
          className="mb-2"
          // Clicks on chips shouldn't bubble to drag handlers.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {issue.linked_documents.length === 1 ? (
            <DocChip
              filename={issue.linked_documents[0].filename}
              onClick={
                canOpen(issue.linked_documents[0])
                  ? () => openDoc(issue.linked_documents[0])
                  : undefined
              }
            />
          ) : (
            <DocChipCollapsed
              filenames={issue.linked_documents.map((d) => d.filename)}
              nonOpenableFilenames={nonOpenableFilenames(issue.linked_documents)}
              onDocClick={(filename) => {
                const doc = issue.linked_documents?.find((d) => d.filename === filename);
                if (doc) openDoc(doc);
              }}
            />
          )}
        </div>
      )}
    </>
  );
});

const CardAssignees = observer(function CardAssignees({
  issue,
  interactive,
  hasTags,
}: CardFieldsProps) {
  const { t } = useLanguage();
  const assignees = issue.assignees ?? [];
  return (
    <>
      {interactive ? (
        <CardControl className="-ml-1 flex items-center gap-0.5">
          <AssigneeCell
            issue={issue}
            emptyLabel={<EmptyCardField icon={UserPlus} label={t.pm.issues.detail.assign} />}
          />
          {!hasTags && (
            <DisciplineCell
              issue={issue}
              emptyLabel={<EmptyCardField icon={Tag} label={t.pm.issues.detail.addTag} />}
            />
          )}
        </CardControl>
      ) : (
        <div className="flex -space-x-1.5">
          {assignees.slice(0, 3).map((u) => (
            <AssigneeAvatar key={u.id} user={u} size={20} className="ring-1 ring-background" />
          ))}
          {assignees.length > 3 && (
            <span className="ml-1 inline-flex items-center text-[10px] font-medium text-muted-foreground">
              +{assignees.length - 3}
            </span>
          )}
        </div>
      )}
    </>
  );
});

const CardDueDate = observer(function CardDueDate({
  issue,
  interactive,
}: Pick<CardFieldsProps, "issue" | "interactive">) {
  const { t } = useLanguage();
  const overdue = isIssueOverdue(issue);
  return (
    <>
      {interactive ? (
        <CardControl className="-mr-1">
          <DueDateCell
            issue={issue}
            emptyLabel={<EmptyCardField icon={Calendar} label={t.pm.issues.detail.setDueDate} />}
          />
        </CardControl>
      ) : (
        issue.due_date && (
          <span
            className={cn(
              "text-[10px]",
              overdue ? "font-medium text-orange-600" : "text-muted-foreground"
            )}
          >
            {formatDate(issue.due_date)}
          </span>
        )
      )}{" "}
    </>
  );
});

/**
 * The card's title, renamed in place with the pencil that appears on hover. Enter or clicking away saves, Escape cancels, Shift+Enter
 * starts a new line. A single click still opens the issue.
 */
const CardTitle = observer(function CardTitle({ issue }: { issue: PMIssue }) {
  const { t } = useLanguage();
  const issuesStore = useIssuesStore();
  const [draft, setDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const ta = inputRef.current;
    if (!ta || draft === null) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [draft]);

  const begin = () => {
    setDraft(issue.title);
    requestAnimationFrame(() => {
      const ta = inputRef.current;
      if (!ta) return;
      ta.focus();
      ta.select();
    });
  };

  const finish = (keep: boolean) => {
    if (draft === null) return;
    const next = draft.trim();
    setDraft(null);
    // Unchanged includes the stored title behind a localised display title:
    // writing that back would retire a document parent issue's automatic title.
    if (!keep || !next || isTitleUnchanged(issue, next)) return;
    const previous = issue.title;
    issuesStore.updateIssueOptimistic(issue.id, { title: next });
    issuesStore.updateIssue(issue.id, { title: next }).catch((err: unknown) => {
      issuesStore.updateIssueOptimistic(issue.id, { title: previous });
      void issuesStore.loadIssues(issue.building_id);
      toast.error(friendlyIssueError(err, t));
    });
  };

  if (draft !== null) {
    return (
      <CardControl className="mb-2">
        <textarea
          ref={inputRef}
          rows={1}
          value={draft}
          aria-label={t.pm.issues.detail.editTitle}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => finish(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              finish(true);
            } else if (e.key === "Escape") {
              e.preventDefault();
              finish(false);
            }
          }}
          className="-mx-1 block w-[calc(100%+0.5rem)] resize-none overflow-hidden rounded bg-background px-1 text-sm font-medium leading-snug outline-none ring-1 ring-ring"
        />
      </CardControl>
    );
  }

  return (
    <div className="relative mb-2">
      <p className="line-clamp-4 min-w-0 whitespace-pre-wrap pr-5 text-sm font-medium leading-snug [overflow-wrap:anywhere]">
        {issue.title}
      </p>
      <CardControl className="absolute right-0 top-0">
        <button
          type="button"
          onClick={begin}
          aria-label={t.pm.issues.detail.editTitle}
          title={t.pm.issues.detail.editTitle}
          className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/card:opacity-100"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </CardControl>
    </div>
  );
});
